import { MatrixRain } from '@/components/terminal/MatrixRain';
import { isStaticPhase, TerminalConfig, TerminalPhase } from '@/components/terminal/types';
import { AlertCircle, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import styles from './Terminal.module.css';

interface TerminalContentProps {
  phases: TerminalPhase[];
  currentPhase: number;
  typedText: string;
  showCursor: boolean;
  config?: TerminalConfig;
}

const ProgressBar = ({
  content,
  duration = 3000,
  style = 'blocks',
}: {
  content: string;
  duration?: number;
  style?: 'blocks' | 'bar';
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration]);

  if (style === 'blocks') {
    const blocks = '█'.repeat(Math.floor(progress / 5)) + '░'.repeat(20 - Math.floor(progress / 5));
    return (
      <div className="space-y-2">
        <div>{content}</div>
        <div className="font-mono">
          [{blocks}] {Math.floor(progress)}%
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div>{content}</div>
      <div className="w-full bg-green-900/30 rounded-sm">
        <div
          className="h-2 bg-green-500 rounded-sm transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const renderPhase = (phase: TerminalPhase, config?: TerminalConfig) => {
  switch (phase.type) {
    case 'matrix':
      return <MatrixRain />;
    case 'table':
      return (
        <div className={`${styles['font-mono']} text-sm mt-4`}>
          <table className="w-full">
            <thead>
              <tr>
                {phase.header?.map((header, i) => (
                  <th key={i} className="text-left px-4 py-2 border border-green-500">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {phase.data?.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-2 border border-green-500">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'access':
      return (
        <div className="flex items-center gap-2 font-bold text-green-400">
          <Shield className="w-5 h-5" />
          {phase.content}
        </div>
      );
    case 'warning':
      return (
        <div className="flex items-center gap-2 font-bold text-red-500">
          <AlertCircle className="w-5 h-5" />
          {phase.content}
        </div>
      );
    case 'progress':
      return (
        <ProgressBar
          content={phase.content}
          duration={phase.duration}
          style={config?.progressStyle}
        />
      );
    default:
      return <div className="whitespace-pre-line">{phase.content}</div>;
  }
};

export const TerminalContent = ({
  phases,
  currentPhase,
  typedText,
  showCursor,
  config,
}: TerminalContentProps) => (
  <div className={styles.phaseContainer}>
    {currentPhase > 0 &&
      phases.slice(0, currentPhase).map((phase, index) => (
        <div key={index} className={styles.phase}>
          {renderPhase(phase, config)}
        </div>
      ))}

    {currentPhase >= 0 && currentPhase < phases.length && (
      <div className={styles.phase}>
        {isStaticPhase(phases[currentPhase].type) ? (
          renderPhase(phases[currentPhase], config)
        ) : (
          <>
            {typedText}
            <span className={showCursor ? styles.cursorVisible : styles.cursorHidden}>_</span>
          </>
        )}
      </div>
    )}
  </div>
);
